import { useMemo } from "react"
import { GetInitialDataCtx, WidgetProps } from "../../contracts/widget"
import { multiCalendarWidgetConfig, MultiCalendarWidgetConfig } from "./config"
import { WidgetProvider } from "../../providers/widget"
import WidgetSection from "../../components/WidgetSection"
import Stylesheet from "../../components/Stylesheet"
import WebFont from "../../components/WebFont"
import MultiCalendarGrid from "./components/MultiCalendarGrid"
import "./multi-calendar.css"
import { resolveWidgetSpacingStyle } from "@homerunner-next/widget-core/spacing"
import { parseWidgetConfig } from "@homerunner-next/widget-core/schema"
import { hostFontSizeCss } from "../../common/host-scope"

export const getInitialData = async (_ctx: GetInitialDataCtx<MultiCalendarWidgetConfig>) => {
  return {}
}

/**
 * Dark-palette overrides for the auto FIRST-PAINT path (#900). The `cssVars`
 * template below always emits the LIGHT palette (so light output stays
 * byte-identical); this appends the DARK palette under two selector prefixes:
 *   - `:host([data-hr-theme="dark"])` — the runtime marker (explicit dark +
 *     post-hydration auto), and the mirrored elevated-dialog host.
 *   - inside `@media (prefers-color-scheme: dark)`,
 *     `:host([data-hr-scheme="auto"]:not([data-hr-theme]))` — a server-stamped
 *     auto widget on a dark OS, painting dark AT PARSE with no JS/cookie; the
 *     `:not([data-hr-theme])` (kept INSIDE `:host(...)`) drops it the instant
 *     the runtime stamps the marker at hydration.
 * multi-calendar.css keys nothing on `.dark` — the whole palette is these
 * inline `--hr-mc-*` vars, whose light fallbacks are hardcoded hex — so
 * mirroring the container var block (from `darkModeColors` directly, NOT the
 * resolvedTheme-gated `colors`) is the complete fix. Values are reused
 * identically to the dark branch of `cssVars`. `:host(...)` (not a descendant
 * `[data-hr-theme]`) is required to reach the shadow host from inside its own
 * stylesheet.
 */
function multiCalendarDarkOverrides(prefix: string, dc: MultiCalendarWidgetConfig['darkModeColors']): string {
  return `
    ${prefix} .hr-multical-container {
      --hr-mc-fg: ${dc.foreground};
      --hr-mc-muted: ${dc.mutedForeground};
      --hr-mc-border: ${dc.border};
      --hr-mc-header-bg: ${dc.headerBackground};
      --hr-mc-row-bg: ${dc.rowBackground};
      --hr-mc-avail-bg: ${dc.availableBackground};
      --hr-mc-avail-fg: ${dc.availableColor};
      --hr-mc-booked-bg: ${dc.bookedBackground};
      --hr-mc-booked-fg: ${dc.bookedColor};
      --hr-mc-price: ${dc.priceColor};
      --hr-mc-selected-bg: ${dc.selectedBackground};
      --hr-mc-selected-fg: ${dc.selectedColor};
      --hr-mc-hover-bg: ${dc.hoverBackground};
      --hr-mc-disabled-bg: ${dc.disabledBackground};
      --hr-mc-notice-bg: ${dc.noticeBackground};
      --hr-mc-notice-fg: ${dc.noticeColor};
      --hr-mc-accent: ${dc.accent};
      color: ${dc.foreground};
      color-scheme: dark;
    }
  `
}

export default function MultiCalendarWidget({ options, feedId, widgetId, resolvedTheme, data }: WidgetProps<MultiCalendarWidgetConfig>) {
  const parsedOptions = useMemo(() => parseWidgetConfig(multiCalendarWidgetConfig, options), [options])
  const colors = useMemo(
    () => (resolvedTheme === 'dark' ? parsedOptions.darkModeColors : parsedOptions.lightModeColors),
    [resolvedTheme, parsedOptions.lightModeColors, parsedOptions.darkModeColors],
  )

  const cssVars = useMemo(() => `
    .hr-multical-container, .hr-multical-container * {
      font-family: '${parsedOptions.font.family}' !important;
    }
    ${hostFontSizeCss(widgetId, parsedOptions.font.size)}
    .hr-multical-container {
      --hr-mc-fg: ${colors.foreground};
      --hr-mc-muted: ${colors.mutedForeground};
      --hr-mc-border: ${colors.border};
      --hr-mc-header-bg: ${colors.headerBackground};
      --hr-mc-row-bg: ${colors.rowBackground};
      --hr-mc-avail-bg: ${colors.availableBackground};
      --hr-mc-avail-fg: ${colors.availableColor};
      --hr-mc-booked-bg: ${colors.bookedBackground};
      --hr-mc-booked-fg: ${colors.bookedColor};
      --hr-mc-price: ${colors.priceColor};
      --hr-mc-selected-bg: ${colors.selectedBackground};
      --hr-mc-selected-fg: ${colors.selectedColor};
      --hr-mc-hover-bg: ${colors.hoverBackground};
      --hr-mc-disabled-bg: ${colors.disabledBackground};
      --hr-mc-notice-bg: ${colors.noticeBackground};
      --hr-mc-notice-fg: ${colors.noticeColor};
      --hr-mc-accent: ${colors.accent};
      color: ${colors.foreground};
      ${resolvedTheme === 'dark' ? 'color-scheme: dark;' : ''}
    }
    /* Dark palette for the auto first-paint path (#900) plus the runtime
       marker. The base rule above always emits LIGHT; these override only under
       the dark host markers, so light rendering is byte-identical to before. */
    ${multiCalendarDarkOverrides(':where(:host([data-hr-theme="dark"]))', parsedOptions.darkModeColors)}
    @media (prefers-color-scheme: dark) {
      ${multiCalendarDarkOverrides(':where(:host([data-hr-scheme="auto"]:not([data-hr-theme])))', parsedOptions.darkModeColors)}
    }
    ${parsedOptions.customCss ?? ''}
  `, [widgetId, parsedOptions.font, parsedOptions.customCss, parsedOptions.darkModeColors, colors, resolvedTheme])

  return (
    <WidgetProvider options={parsedOptions} feedId={feedId} widgetId={widgetId} resolvedTheme={resolvedTheme} widgetType="multi-calendar" data={data ?? {}}>
      <div
        className={`hr-multical-container${resolvedTheme === 'dark' ? ' dark' : ''}`}
        style={resolveWidgetSpacingStyle(parsedOptions)}
        suppressHydrationWarning
      >
        <WebFont />
        <Stylesheet css={cssVars} />
        <WidgetSection show={parsedOptions.section?.show} title={parsedOptions.section?.title} forceHeadingInMarkup />
        <MultiCalendarGrid />
      </div>
    </WidgetProvider>
  )
}
